import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import { estimateRange } from '@/lib/utils/scan-estimate'
import { useScanEstimate, useScanSettings } from '../scan-requests'

/**
 * "Scan before copying to egress", on a recovery request.
 *
 * Andrew, 2026-10-06: "when the users request a recovery there is an option to scan the objects
 * before they are copied to egress. This is a change in the process." Ticked, the request's
 * versions are scanned first, the results are accepted on the approval screen, and only then can
 * the retrieval be approved - nothing leaves the vault before that.
 *
 * Always offered - it is not a system setting (Andrew, 7 Oct 2026: "It should always be on").
 * Starts ticked when the default setting says so.
 */
interface Props {
    checked: boolean
    onChange: (checked: boolean) => void
    /** The versions the request would copy, when the screen knows them: gives an estimate. */
    versionIds?: Array<number | null | undefined>
    disabled?: boolean
}

export function ScanFirstOption({ checked, onChange, versionIds = [], disabled }: Props) {
    const { t } = useTranslation()
    const { data: settings } = useScanSettings()
    const estimate = useScanEstimate(versionIds, checked)
    const range = estimateRange(estimate.data, t)

    // The setting's default, applied once when it arrives - never over a choice already made.
    const applied = useRef(false)
    useEffect(() => {
        if (!settings || applied.current) return
        applied.current = true
        if (settings.defaultOn) onChange(true)
    }, [settings, onChange])

    return (
        <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer">
            <input
                type="checkbox"
                className="mt-1 h-4 w-4 flex-shrink-0"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium">
                    <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                    {t('scanRequests.scanFirst')}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">{t('scanRequests.scanFirstHelp')}</span>
                {checked && range && <span className="block text-xs mt-1">{t('scanRequests.estimate', { range })}</span>}
            </span>
        </label>
    )
}
